import { scaleProfile, type TerrainProfile } from '../../engine/terrain/TerrainProfile';
import { VERDANT_STYLE, verdantForest } from '../../engine/terrain/TerrainStyle';
import { VERDANT_TREES } from '../../engine/vegetation/VegetationStyle';
import type { CaveDef, CaveEndDef, CaveLinkDef } from '../scenes/CaveWorld';
import type { HollowSceneDef } from '../scenes/HollowScene';
import { CAVE_HINTS, EMBERDEEP_MOUTH, GLOWMIRE_MOUTH, UMBRAL_MOUTH, VERDANT_MOUTH } from './caves';
import { EMBERDEEP } from './emberdeep';
import { GLOWMIRE } from './glowmire';
import { UMBRAL } from './umbral';
import { VERDANT } from './verdant';

/*
 * The Geodes: tiny hollows a few hundred metres across, clustered in the rock like
 * bubbles in stone and joined by short caves. Each borrows a big world's look at a
 * fraction of its size. They have no air to speak of (no haze across a few hundred
 * metres), so they share one post pipeline.
 */

/**
 * What every geode shares: no air or clouds, a small atlas, a spawn on low ground. Heights
 * shrink with the land shapes, so slopes stay as walkable as the big world's; `terrain`
 * then adjusts the shrunk recipe.
 */
function geode(
  base: HollowSceneDef,
  over: Partial<HollowSceneDef> & Pick<HollowSceneDef, 'id' | 'name' | 'radius' | 'seed'>,
  terrain: Partial<TerrainProfile> = {},
): HollowSceneDef {
  const k = over.radius / base.radius;
  return {
    ...base,
    terrain: { ...scaleProfile(base.terrain, k, k), ...terrain },
    air: null,
    clouds: null,
    atlasScale: 0.5,
    spawn: { dir: base.spawn.dir, minHeight: 0.5, maxHeight: 20 },
    hints: undefined,
    ...over,
  };
}

/** Birds for a small sky: a few flocks circling low. */
const fewBirds: HollowSceneDef['birds'] = (rig) => ({
  ...VERDANT.birds!(rig),
  flocks: 4,
  perFlock: 5,
  altitude: [8, 24],
  radius: [10, 22],
  speed: 7,
});

/** Verdant's look with forest patches sized for a tiny world (Verdant's are kilometres across). */
const tinyVerdant = (forest: number) => () => ({ ...VERDANT_STYLE, forestMask: verdantForest(forest) });

/** The hub: meadows and copses under a small sun wearing a veil. */
const HEARTH = geode(
  VERDANT,
  {
    id: 'hearth',
    name: 'Hearth',
    center: [5.2e6, 6.1e6, -8.3e6],
    radius: 420,
    seed: 101,
    sun: { radius: 7, color: [1.0, 0.9, 0.76], intensity: 5.5 },
    style: tinyVerdant(90),
    birds: fewBirds,
  },
  { snowLine: 1e9 },
);

/** A glowing marsh, lit by its own plants. */
const GLIMMER = geode(GLOWMIRE, { id: 'glimmer', name: 'Glimmer', radius: 300, seed: 113 });

/** Black rock over pools of lava. */
const CINDER = geode(EMBERDEEP, { id: 'cinder', name: 'Cinder', radius: 280, seed: 127 });

/** No light at all: bring your lantern. */
const HUSH = geode(UMBRAL, { id: 'hush', name: 'Hush', radius: 220, seed: 131, lantern: true });

/** A pond world: mostly water, with a few wooded islands. */
const TARN = geode(
  VERDANT,
  {
    id: 'tarn',
    name: 'Tarn',
    radius: 340,
    seed: 137,
    sun: { radius: 6, color: [0.95, 0.95, 1.0], intensity: 5 },
    style: tinyVerdant(70),
    birds: fewBirds,
  },
  { landBias: -0.12, snowLine: 1e9 },
);

/** Snow to the valleys under a pale sun (the snow line is in Verdant's metres, just above the water). */
const RIME = geode(
  VERDANT,
  {
    id: 'rime',
    name: 'Rime',
    radius: 300,
    seed: 149,
    sun: { radius: 6, color: [0.86, 0.92, 1.0], intensity: 5 },
    veil: { ...VERDANT.veil, duskColor: [0.6, 0.62, 1.0] },
    // Its snow is winter's, down to the valleys, not a mountain's: conifers stand in it (green
    // broadleaf trees stood on it), and no tree line holds them back.
    trees: () => ({ ...VERDANT_TREES, aShare: [1, 1], where: undefined }),
  },
  { snowLine: 30 },
);

/** A cave between two geodes, with mouths where the old 60 m spirals had them. */
function link(
  from: number,
  to: number,
  axis: [number, number, number],
  a: CaveEndDef,
  b: CaveEndDef,
  phase: number,
): CaveLinkDef {
  return {
    from,
    to,
    axis,
    spiralGap: 60,
    tunnel: { tubeRadius: 3.5, helixRadius: 14, pitchDeg: 24, depth: 6, phase },
    a,
    b,
  };
}

/** Six tiny worlds around a meadow hub, each a short cave from its neighbour. */
export const GEODES: CaveDef = {
  id: 'geodes',
  name: 'The Geodes',
  hollows: [HEARTH, GLIMMER, CINDER, TARN, HUSH, RIME],
  links: [
    link(0, 1, [1, 0.25, 0.1], { height: 4, ...VERDANT_MOUTH }, { height: 2, ...GLOWMIRE_MOUTH }, 0.4),
    link(0, 2, [-0.6, 0.3, 0.75], { height: 4, ...VERDANT_MOUTH }, { height: 6, ...EMBERDEEP_MOUTH }, 1.9),
    link(0, 3, [-0.45, -0.25, -0.85], { height: 4, ...VERDANT_MOUTH }, { height: 3, ...VERDANT_MOUTH }, 3.1),
    link(1, 4, [0.55, 0.75, -0.35], { height: 2, ...GLOWMIRE_MOUTH }, { height: 3, ...UMBRAL_MOUTH }, 4.4),
    link(3, 5, [0.15, -0.95, -0.3], { height: 3, ...VERDANT_MOUTH }, { height: 4, ...VERDANT_MOUTH }, 5.6),
  ],
  hints: [
    'Six tiny worlds, each a few hundred metres across. Look up: the far side is just overhead.',
    ...CAVE_HINTS,
  ],
};

/** Each geode on its own (?scene=hearth etc.), for testing. */
export const GEODE_HOLLOWS = {
  hearth: HEARTH,
  glimmer: GLIMMER,
  cinder: CINDER,
  tarn: TARN,
  hush: HUSH,
  rime: RIME,
};
